import React, { useState } from 'react'
import styled from 'styled-components/macro'
import { CSSTransition } from 'react-transition-group'
import { useTranslation } from 'react-i18next'

import { motionDurationNormal } from '@resultadosdigitais/tangram-design-tokens'
import RDStationLogo from './logo-rdstation.svg'
import Bottom from '../LoginForm/Bottom'
import LoginForm from '../LoginForm'
import RedirectToSignup from '../RedirectToSignup'
import ChooseSignup from '../ChooseSignup'

import { usePath } from '@resultadosdigitais/front-hub/react'
import RecoverPasswordForm from '../PasswordRecoverForm'
import RecoverPasswordSuccess from '../RecoverPasswordSuccess'
import MFAMessageSuccess from '../MFAMessageSuccess'
import LanguageDropdown from '../LanguageDropdown'

import useQuery from '../../hooks/useQuery'
import { useInstrumentation } from '@resultadosdigitais/front-hub/react'
import { assembleTrackingParams } from '../../services/utmService/trackingParams'
import snakeToPascal from 'services/snakeToPascal/snakeToPascal'
import SignupLogin from './SignupLogin'
import MFAappChallengeForm from 'components/MFAappChallengeForm'
import DeviceRegistrationForm from 'components/DeviceRegistrationForm/deviceRegistrationForm'
import MFAadoptionScreen from 'components/MFAadoptionScreen/MFAadoptionScreen'
import SideBanner from 'components/SideBanner'
import SSOLoginForm from '../SSOLoginForm'

const PT_BR_LANGUAGE = 'pt-BR'
const RDSM_SIGNUP_DOMAIN = 'https://app.rdstation.com.br/'
const VALID_FORMS = ['LoginForm', 'SSOLoginForm', 'RecoverPasswordForm']

const Header = styled.header`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  background-color: var(--color-white);
  box-shadow: var(--shadow-md);
  height: 64px;
  padding: var(--size-spacing-04);
  align-items: stretch;
  padding-left: var(--size-spacing-04);
`

const Main = styled.div`
  background: var(--color-gray-10);
  display: flex;
  flex: 1;
  flex-direction: row;
`

const RootContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: var(--size-spacing-05);
  padding-bottom: var(--size-spacing-05);
  > * + * {
    margin-top: var(--size-spacing-04);
    @media screen and (min-width: 768px) {
      margin-top: var(--size-spacing-01);
    }
  }
`

const Box = styled.div`
  min-height: 457px;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  margin-top: var(--size-spacing-05);
`

const FadeLeftPanel = styled.div`
  width: 410px !important;
  flex-shrink: 1;
  width: 100%;
  height: auto !important;
  &.enter {
    opacity: 0;
    transform: translateX(-100%);
  }

  &.enter-active {
    opacity: 1;
    transform: translateX(0);
    transition: all ${props => props.timeout}ms;
  }

  &.enter-done {
    opacity: 1;
    transform: translateX(0);
  }

  &.exit {
    opacity: 1;
    transform: translateX(0);
  }

  &.exit-active {
    opacity: 0;
    transform: translateX(-100%);
    transition: all ${props => props.timeout}ms;
  }
`

const FadeRightPanel = styled.div`
  min-width: 408px !important;
  height: auto !important;
  flex-shrink: 1;
  opacity: ${props => props.defaultOpacity ?? '0'};
  &.enter {
    opacity: 0;
    transform: translateX(0);
  }

  &.enter-active {
    opacity: 1;
    transform: translateX(0);
    transition: all ${props => props.timeout}ms;
  }

  &.enter-done {
    opacity: 1;
    transform: translateX(0);
  }

  &.exit {
    opacity: 1;
    transform: translateX(-100%);
  }

  &.exit-active {
    opacity: 0;
    transform: translateX(0);
    transition: all ${props => props.timeout}ms;
  }

  &.fullWidth {
    width: 100% !important;
  }
`
const MfaPanel = styled(FadeRightPanel)`
  width: 600px !important;
  flex-shrink: 1;
  @media only screen and (max-width: 425px) {
    width: 408px !important;
  }
`

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 408px;
  margin-top: var(--size-spacing-06);
`

function Login({ siteKey }) {
  const { t, i18n } = useTranslation()
  const { track } = useInstrumentation()
  const [currentForm, setCurrentForm] = useState(currentFormByParam())
  const [loginData, setLoginData] = useState({})
  const assetPath = usePath()

  const query = useQuery()
  const existAccount = query.get('exist_account') === 'true' ? true : false

  const showSignupAccount = query.get('show_signup_account') === 'true' ? true : false

  function goToPasswordRecoverForm() {
    setCurrentForm('RecoverPasswordForm')
  }

  function goToSSOLoginForm() {
    setCurrentFormQueryString('SSOLoginForm')
    setCurrentForm('SSOLoginForm')
  }

  function goToMFAadoptionScreen({ redirectTo, isAdmin }) {
    setLoginData({ redirectTo, isAdmin })
    setCurrentForm('MFAadoptionScreen')
  }

  function goToMFAMessage() {
    setCurrentForm('MFAMessageSuccess')
  }

  function goToDeviceRegistrationForm({ email }) {
    setLoginData({ email })
    setCurrentForm('DeviceRegistrationForm')
  }

  function goToLoginForm() {
    setCurrentForm('LoginForm')
  }

  function goToMfaChallengeForm({ email, enable_mfa_app, recovery_by_admin_link }) {
    setLoginData({ email, enableMfaApp: enable_mfa_app, recoveryByAdminLink: recovery_by_admin_link })
    setCurrentForm('MFAappChallengeForm')
  }

  function goToRecoverPasswordSuccess() {
    setCurrentForm('RecoverPasswordSuccess')
  }

  let timeout = Number(motionDurationNormal.replace('ms', ''))

  function renderChooseSignup() {
    const chooseSignup = true
    const url = `${RDSM_SIGNUP_DOMAIN}signup?locale=${i18n.language}&${assembleTrackingParams(chooseSignup)}`

    i18n.language === PT_BR_LANGUAGE ? setCurrentForm('ChooseSignup') : (window.location.href = url)
  }

  return (
    <>
      {showSignupAccount ? (
        <SignupLogin />
      ) : (
        <>
          <Header>
            <img src={assetPath(RDStationLogo)} alt={t('Login.Acessibility.LogoAlt')} />
          </Header>
          <Main>
            <SideBanner isLoginForm={currentForm === 'LoginForm'} />
            <RootContent>
              {shouldDisplayRedirectToSignup(existAccount, currentForm) && <RedirectToSignup onRedirect={renderChooseSignup} />}
              <Box>
                <CSSTransition in={currentForm === 'LoginForm'} timeout={timeout} unmountOnExit>
                  <FadeLeftPanel timeout={timeout}>
                    <LoginForm
                      showSSOLoginScreen={goToSSOLoginForm}
                      showPasswordRecoverScreen={goToPasswordRecoverForm}
                      showMFAChallengeScreen={goToMfaChallengeForm}
                      showMFAScreen={goToMFAMessage}
                      showDeviceRegistrationScreen={goToDeviceRegistrationForm}
                      showMFAadoptionScreen={goToMFAadoptionScreen}
                      existAccount={existAccount}
                      siteKey={siteKey}
                    />
                  </FadeLeftPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'SSOLoginForm'} timeout={timeout} unmountOnExit>
                  <FadeRightPanel timeout={timeout} defaultOpacity={currentFormByParam() === 'SSOLoginForm' ? '1' : '0'}>
                    <SSOLoginForm goToLoginForm={goToLoginForm} />
                  </FadeRightPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'MFAadoptionScreen'} timeout={timeout} unmountOnExit>
                  <MfaPanel timeout={timeout}>
                    <MFAadoptionScreen redirectTo={loginData.redirectTo} isAdmin={loginData.isAdmin} />
                  </MfaPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'MFAMessageSuccess'} timeout={timeout} unmountOnExit>
                  <FadeRightPanel timeout={timeout}>
                    <MFAMessageSuccess showLogin={goToLoginForm} />
                  </FadeRightPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'DeviceRegistrationForm'} timeout={timeout} unmountOnExit>
                  <MfaPanel timeout={timeout}>
                    <DeviceRegistrationForm goToLoginForm={goToLoginForm} email={loginData.email} />
                  </MfaPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'RecoverPasswordForm'} timeout={timeout} unmountOnExit>
                  <FadeRightPanel timeout={timeout} defaultOpacity={currentFormByParam() === 'RecoverPasswordForm' ? '1' : '0'}>
                    <RecoverPasswordForm onBackButtonClick={goToLoginForm} onSuccess={goToRecoverPasswordSuccess} />
                  </FadeRightPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'MFAappChallengeForm'} timeout={timeout} unmountOnExit>
                  <MfaPanel timeout={timeout} defaultOpacity={currentFormByParam() === 'MFAappChallengeForm' ? '1' : '0'}>
                    <MFAappChallengeForm
                      goToLoginForm={goToLoginForm}
                      email={loginData.email}
                      enableMfaApp={loginData.enableMfaApp}
                      recoveryByAdminLink={loginData.recoveryByAdminLink}
                    />
                  </MfaPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'RecoverPasswordSuccess'} timeout={timeout} unmountOnExit>
                  <FadeRightPanel timeout={timeout}>
                    <RecoverPasswordSuccess />
                  </FadeRightPanel>
                </CSSTransition>
                <CSSTransition in={currentForm === 'ChooseSignup'} timeout={timeout} unmountOnExit>
                  <FadeRightPanel timeout={timeout} className="fullWidth">
                    <ChooseSignup onClick={goToLoginForm} onTrack={track} />
                  </FadeRightPanel>
                </CSSTransition>
                {shouldDisplayLanguageDropdown(currentForm) && (
                  <StyledDiv>
                    <Bottom />
                    <LanguageDropdown />
                  </StyledDiv>
                )}
              </Box>
            </RootContent>
          </Main>
        </>
      )}
    </>
  )
}

function setCurrentFormQueryString(formName) {
  const url = new URL(window.location)
  url.searchParams.set('current_form', formName)
  window.history.pushState({}, '', url)
}

function currentFormByParam() {
  const urlParams = new URLSearchParams(window.location.search)
  const currentForm = urlParams.get('current_form')
  if (currentForm) {
    const currentFormName = snakeToPascal(urlParams.get('current_form'))
    return validateDefaultForm(currentFormName) ? currentFormName : 'LoginForm'
  }
  return 'LoginForm'
}

function validateDefaultForm(formName) {
  return VALID_FORMS.includes(formName)
}

function shouldDisplayLanguageDropdown(currentForm) {
  return !['ChooseSignup', 'MFAappChallengeForm'].includes(currentForm)
}

function shouldDisplayRedirectToSignup(existAccount, currentForm) {
  return !existAccount && ['LoginForm', 'RecoverPasswordForm'].includes(currentForm)
}

export default Login
